// Ambient inline hashtag editing: tags render as chips (each with a remove ×) plus a trailing
// input to add more. Enter / space / comma commits the draft; Backspace on an empty input
// removes the last chip. Commits via onCommit (the caller wires updateCreativeContent, which
// persists + re-scores). Tags are stored WITHOUT the leading '#'.

import { useRef, useState } from 'react'
import { X } from 'lucide-react'

/** Normalize a typed tag: drop a leading '#', strip whitespace/extra '#', collapse spaces. */
function normalizeTag(raw: string): string {
  return raw.replace(/^#+/, '').replace(/\s+/g, '').trim()
}

export function EditableHashtags({
  value,
  onCommit,
  className,
  placeholder = 'Add hashtag',
}: {
  value: string[]
  onCommit: (tags: string[]) => void
  /** Text color class for the chips (platform hashtag color). */
  className?: string
  placeholder?: string
}) {
  const [draft, setDraft] = useState('')

  // Mirror the committed tags so an add (input onBlur) and a chip remove (× onClick) that fire in
  // the SAME tick compose, instead of both deriving from a stale `value` — otherwise whichever
  // onCommit lands last clobbers the other (dropping the just-typed tag, or resurrecting a removed
  // chip). Re-synced to `value` every render so external/AI updates still flow in.
  const latest = useRef(value)
  latest.current = value
  const commit = (next: string[]) => {
    latest.current = next
    onCommit(next)
  }

  const addDraft = () => {
    const t = normalizeTag(draft)
    setDraft('')
    if (!t) return
    if (latest.current.some(v => v.toLowerCase() === t.toLowerCase())) return
    commit([...latest.current, t])
  }
  const removeAt = (i: number) => commit(latest.current.filter((_, idx) => idx !== i))

  return (
    <div
      className={`flex flex-wrap items-center gap-x-1.5 gap-y-1 ${className ?? ''}`}
      onClick={e => e.stopPropagation()}
    >
      {value.map((tag, i) => (
        <span key={`${tag}-${i}`} className="group/tag inline-flex items-center gap-0.5">
          <span>#{tag}</span>
          <button
            type="button"
            aria-label={`Remove #${tag}`}
            onClick={() => removeAt(i)}
            className="rounded-full p-0.5 opacity-0 transition-opacity group-hover/tag:opacity-100 hover:bg-black/10"
          >
            <X size={11} />
          </button>
        </span>
      ))}
      <input
        value={draft}
        onChange={e => setDraft(e.target.value)}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ' || e.key === ',') {
            e.preventDefault()
            addDraft()
          } else if (e.key === 'Backspace' && draft === '' && latest.current.length > 0) {
            e.preventDefault()
            removeAt(latest.current.length - 1)
          }
        }}
        onBlur={addDraft}
        placeholder={value.length === 0 ? placeholder : ''}
        aria-label="Add hashtag"
        className="min-w-[80px] flex-1 bg-transparent text-sm outline-none placeholder:text-gray-400"
      />
    </div>
  )
}
